/**
 * Durak bot.
 *
 * - Leading: its lowest non-trump card, preferring a rank it holds more than once (so it can keep the bout going).
 * - Throwing in: dumps low non-trump cards of a rank on the table freely, and trumps only when the defender is about
 *   to go out; passes otherwise. When the defender is taking, every matching non-trump card is a free dump.
 * - Defending: the cheapest card that beats the attack. It takes instead of spending a trump on a low card early in
 *   the game, or when beating would cost it a high trump it needs later.
 * The bot only ever reads its own seat's view.
 */
import { parseCard, type CardId } from '../../core/cards.js';
import { chance, type BotEngine } from '../../bots/types.js';
import { durakAttackCards, type DurakAction, type DurakLegal, type DurakView } from './engine.js';

const rank = (c: CardId) => parseCard(c).rank;
const suit = (c: CardId) => parseCard(c).suit;

function cheapest(view: DurakView, cards: readonly CardId[]): CardId {
  const cost = (c: CardId) => (suit(c) === view.trump ? 100 : 0) + rank(c);
  return [...cards].sort((a, b) => cost(a) - cost(b))[0]!;
}

function attack(view: DurakView, legal: DurakLegal): DurakAction {
  const cards = legal.attack;
  if (view.table.length === 0) {
    const count = (c: CardId) => view.hand.filter((h) => rank(h) === rank(c)).length;
    const plain = cards.filter((c) => suit(c) !== view.trump);
    const pool = plain.length > 0 ? plain : cards;
    const best = [...pool].sort((a, b) => count(b) - count(a) || Number(suit(a) === view.trump) - Number(suit(b) === view.trump) || rank(a) - rank(b))[0]!;
    // Prefer the lowest card unless a pair clearly helps: a pair is only worth it if it is not much higher.
    const lowest = cheapest(view, pool);
    return { type: 'attack', card: count(best) > 1 && rank(best) <= rank(lowest) + 3 ? best : lowest };
  }
  const plain = cards.filter((c) => suit(c) !== view.trump);
  if (view.taking) return plain.length > 0 ? { type: 'attack', card: cheapest(view, plain) } : { type: 'pass' };
  const defenderLow = view.handCounts[view.defender]! <= 2 && view.stockCount === 0;
  if (plain.length > 0 && (rank(cheapest(view, plain)) <= 10 || defenderLow)) return { type: 'attack', card: cheapest(view, plain) };
  if (defenderLow && cards.length > 0) return { type: 'attack', card: cheapest(view, cards) };
  return { type: 'pass' };
}

function defend(view: DurakView, legal: DurakLegal): DurakAction {
  if (legal.defend.length === 0) return { type: 'take' };
  const card = cheapest(view, legal.defend);
  const open = view.table.find((p) => p.defense === null)!;
  if (suit(card) === view.trump && suit(open.attack) !== view.trump) {
    const early = view.stockCount > 4;
    const lowAttack = rank(open.attack) <= 9;
    const heavy = view.hand.length >= 8;
    // Spending a trump on a low card early just wastes it, but a full table is not worth taking either.
    if (early && lowAttack && !heavy && view.table.length <= 2) return { type: 'take' };
  }
  return { type: 'defend', card };
}

export const durakBot: BotEngine<DurakView, DurakLegal, DurakAction> = {
  decideAction(view, legal, ctx) {
    return this.chooseCard!(view, legal, ctx);
  },

  chooseCard(view, legal, ctx) {
    if (view.phase === 'DEFEND') {
      if (legal.defend.length > 0 && chance(ctx, ctx.profile.mistakeRate)) {
        return { type: 'defend', card: legal.defend[ctx.rng.int(legal.defend.length)]! };
      }
      return defend(view, legal);
    }
    if (legal.attack.length === 0) return { type: 'pass' };
    if (view.table.length === 0 && chance(ctx, ctx.profile.mistakeRate)) {
      return { type: 'attack', card: legal.attack[ctx.rng.int(legal.attack.length)]! };
    }
    return attack(view, legal);
  },

  evaluateState(view) {
    if (view.seat === null) return 0;
    const mine = view.handCounts[view.seat]!;
    const others = view.handCounts.filter((_, s) => s !== view.seat);
    const avg = others.reduce((a, b) => a + b, 0) / others.length;
    return Math.max(0, Math.min(1, 0.5 + (avg - mine) / 20));
  },

  calculateRisk(view, legal) {
    return view.phase === 'DEFEND' && legal.defend.length === 0 ? 1 : 0.3;
  },

  handleTimeout(view, legal) {
    if (view.phase === 'DEFEND') return { type: 'take' };
    if (view.table.length > 0) return { type: 'pass' };
    const cards = legal.attack.length > 0 ? legal.attack : durakAttackCards(view.hand, view.table, view.limit);
    return { type: 'attack', card: cheapest(view, cards) };
  },
};
